import React, { useMemo, useState } from 'react';
import {
  ButtonDropdown,
  DangerText,
  NumberField,
  SelectField,
  TDropdownElementProps,
  Currency,
  TextField,
  Tooltip,
} from 'ui-toolkit';
import { depositCategoryOptions, prettyDepositCategory } from 'types/order/escrow/eDepositCategory';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { StyledTableNumberColumn, StyledTableRow, StyledTableRowColumn } from '../table/StyledTable';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { achPullPayerTypeOptions, prettyAchPullPayerType } from 'types/order/escrow/eAchPullPayerType';
import { MatchingACHModalSection } from './MatchingModal/MatchingACHModalSection';
import { IEscrowExpectedIncomingAchData } from '../../../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { DepositTooltipContent } from './DepositTooltipContent';
import { useField } from 'formik';
import { useHasPermissions } from '../../../accessControl/hooks/useHasPermissions';

interface IIncomingACHDepositRowProps {
  fieldName: string;
  index: number;
  onCancelEdit: () => void;
  onDelete: () => void;
  onSend: () => void;
  newDeposit: boolean;
  achPullDeposit: IEscrowExpectedIncomingAchData;
  remove: () => void;
  children?: React.ReactNode;
}
export const IncomingACHDepositRow: React.FC<IIncomingACHDepositRowProps> = ({
  fieldName,
  index,
  onCancelEdit,
  onDelete,
  newDeposit,
  achPullDeposit,
  remove,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresEdit]);
  const [{ value: isEditing }, , { setValue: setIsEditing }] = useField<boolean>(`${fieldName}.${index}.isEditing`);

  const kabobItems: TDropdownElementProps[] = useMemo(() => {
    const items: TDropdownElementProps[] = [];
    if (isEditing) {
      items.push({
        children: 'Cancel Edit',
        onClick: () => {
          onCancelEdit();
        },
        'data-test-id': `cancel-edit-ach-${index}`,
      });
    }
    if (!isEditing) {
      items.push(
        {
          children: 'Edit Item',
          onClick: () => setIsEditing(true),
          'data-test-id': `edit-ach-${index}`,
        },
        {
          children: 'Match',
          onClick: () => setMatchACHModal(true),
        }
      );
    }

    items.push({
      children: <DangerText>Delete</DangerText>,
      onClick: newDeposit ? remove : onDelete,
      'data-test-id': `delete-ach-${index}`,
    });
    return items;
  }, [onDelete, setIsEditing, isEditing, onCancelEdit, index, newDeposit, remove]);

  const [matchACHModal, setMatchACHModal] = useState(false);

  return (
    <>
      <Tooltip
        trigger={'hover'}
        placement={'top'}
        tooltipContent={
          <DepositTooltipContent
            createdAt={achPullDeposit.createdAt}
            createdBy={achPullDeposit.createdBy}
            updatedAt={achPullDeposit.updatedAt}
            updatedBy={achPullDeposit.updatedBy}
          />
        }
      >
        <StyledTableRow>
          <StyledTableNumberColumn>{(index + 1).toString().padStart(2, '0')}</StyledTableNumberColumn>
          {isEditing && (
            <>
              <TextField
                className={`p-4`}
                name={`${fieldName}.${index}.wellsFargoTransactionNumber`}
                placeholder='StreetAddress-Zip (ex "123MainSt-90210")'
                readOnly={!hasEditPermission}
              />
              <NumberField
                className={`p-4`}
                name={`${fieldName}.${index}.amountCents`}
                mode="currency"
                placeholder="1,234.56"
                valueIsCents
                disabled={!hasEditPermission}
              />
              <SelectField
                className={`p-4`}
                name={`${fieldName}.${index}.category`}
                options={depositCategoryOptions}
                isDisabled={!hasEditPermission}
              />
              <SelectField
                className={`p-4`}
                name={`${fieldName}.${index}.payerType`}
                options={achPullPayerTypeOptions}
                isDisabled={!hasEditPermission}
              />
            </>
          )}
          {!isEditing && (
            <>
              <StyledTableRowColumn>{achPullDeposit.wellsFargoTransactionNumber}</StyledTableRowColumn>
              <StyledTableRowColumn>
                <Currency value={achPullDeposit.amountCents} valueIsCents />
              </StyledTableRowColumn>
              <StyledTableRowColumn>
                {achPullDeposit.category ? prettyDepositCategory[achPullDeposit.category] : ''}
              </StyledTableRowColumn>
              <StyledTableRowColumn>{prettyAchPullPayerType[achPullDeposit.payerType]}</StyledTableRowColumn>
            </>
          )}
          <div className={`self-center justify-self-center`}>
            {hasEditPermission && (
              <ButtonDropdown intent={'ghost'} isInline items={kabobItems} data-test-id={`ach-kabob`}>
                <StyledIcon icon={faEllipsisV} />
              </ButtonDropdown>
            )}
          </div>
        </StyledTableRow>
      </Tooltip>
      {matchACHModal && (
        <MatchingACHModalSection onClose={() => setMatchACHModal(false)} expectedACH={achPullDeposit} />
      )}
    </>
  );
};

const StyledIcon = styled(FontAwesomeIcon)`
  padding-left: ${(props) => props.theme.spacing.roomyContentPaddingValue}px;
  padding-right: ${(props) => props.theme.spacing.roomyContentPaddingValue}px;
`;
